import React, { useEffect, useRef, useState } from 'react';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { SnappingUnderlay } from '@paragon/contract-library';
import { point2DDistanceTo, point2DEquals } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';

import { DeletionActionFactory, getDeletionProps } from '../../../../store/actions/deletionActions';
import {
  cancelPickingUnderlayScale,
  saveSnappingUnderlay,
  startPickingUnderlayOrigin,
  startPickingUnderlayScale,
} from '../../../../store/actions/layout/snappingUnderlayActions';
import { isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import { getSnappingUnderlayTakeoverMode } from '../../../../store/selectors/snappingUnderlaySelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { clearPickingObject } from '../../../../store/slices/applicationSlice';
import { snappingUnderlaySlice } from '../../../../store/slices/snappingUnderlaySlice';
import { focusCameraToVisibleSnappingGuideLines } from '../../../../three/ThreeJsSceneActions';
import { DocumentRotationField } from '../../../controls/DocumentRotationField';
import { ScaleRatioField } from '../../../controls/ScaleRatioField';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { LayoutPointPickerField } from '../../subpanels/insertions/truss-envelope/LayoutPointPickerField';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';

export function SnappingUnderlayProperties(props: { value: SnappingUnderlay }) {
  const { value } = props;

  const dispatch = useAppDispatch();

  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const deletionProps = useAppSelector(getDeletionProps);
  const deletionActionFactory = new DeletionActionFactory(
    [{ type: 'snappingUnderlay', guid: value.guid }],
    deletionProps,
  );

  const mode = useAppSelector(getSnappingUnderlayTakeoverMode);
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  if (mode === 'picking-scale') {
    return (
      <PanelLayout underlay={value} editable={false} dispatch={dispatch}>
        <ScalePickingContent underlay={value} />
      </PanelLayout>
    );
  }

  if (mode === 'picking-origin') {
    return (
      <PanelLayout underlay={value} editable={false} dispatch={dispatch}>
        <Alert severity="info" variant="outlined">
          Select a point in the underlay that corresponds to the scene origin (0,0) in the layout.
        </Alert>

        <Button fullWidth size="small" variant="outlined" onClick={() => dispatch(clearPickingObject())}>
          Cancel
        </Button>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout underlay={value} editable={!viewOnly} dispatch={dispatch}>
      <ScaleRatioField
        scale={value.scale}
        onSubmit={(scale) => {
          dispatch(saveSnappingUnderlay({ ...value, scale }));
        }}
        actionButton={
          <Button
            variant="outlined"
            sx={{ marginBottom: '2px' }}
            disabled={viewOnly || !!pickingObjectInfo}
            startIcon={<AspectRatioIcon />}
            onClick={() => {
              dispatch(startPickingUnderlayScale(value.guid));
            }}
          >
            Pick Scale
          </Button>
        }
      />

      <div>
        <Typography variant="h6">Offset:</Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <DistanceField
            sx={{ marginBottom: 0 }}
            label="X"
            value={value.translate.x}
            onSubmit={(x) => {
              dispatch(saveSnappingUnderlay({ ...value, translate: { x, y: value.translate.y } }));
            }}
          />

          <DistanceField
            sx={{ marginBottom: 0 }}
            label="Y"
            value={value.translate.y}
            onSubmit={(y) => {
              dispatch(saveSnappingUnderlay({ ...value, translate: { x: value.translate.x, y } }));
            }}
          />

          <Button
            fullWidth
            sx={{ marginBottom: '1px' }}
            variant="outlined"
            disabled={viewOnly || !!pickingObjectInfo}
            startIcon={<ControlCameraIcon />}
            onClick={() => {
              dispatch(startPickingUnderlayOrigin(value.guid));
            }}
          >
            Pick Origin
          </Button>
        </Box>
      </div>

      <Divider />

      <DocumentRotationField
        value={value.rotation}
        onSubmit={(rotation) => {
          dispatch(saveSnappingUnderlay({ ...value, rotation }));
        }}
      />

      <Divider />

      <Stack direction="row" sx={{ marginY: 1 }}>
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          sx={{ marginLeft: 'auto' }}
          disabled={!deletionActionFactory.isDeletionFeasible()}
          onClick={() => {
            dispatch(deletionActionFactory.getDeletionAction());
          }}
        >
          Delete Snapping Underlay
        </Button>
      </Stack>
    </PanelLayout>
  );
}

function PanelLayout(props: {
  underlay: SnappingUnderlay;
  editable: boolean;
  dispatch: ReturnType<typeof useAppDispatch>;
  children: React.ReactNode;
}) {
  return (
    <PropertiesPanel
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      title="Snapping Underlay Properties"
      titleButton={<PropertiesPanelMenuButton type="snappingUnderlay" guid={props.underlay.guid} />}
    >
      <PropertyTextInput
        disabled={!props.editable}
        sx={{ marginTop: 4 }}
        property="Name"
        value={props.underlay.name}
        submit={(name) => {
          props.dispatch(saveSnappingUnderlay({ ...props.underlay, name }));
        }}
      />

      {props.children}
    </PropertiesPanel>
  );
}

function ScalePickingContent(props: { underlay: SnappingUnderlay }) {
  const dispatch = useAppDispatch();
  const [distance, setDistance] = useState<number | null>(null);
  const [distanceInputHasBegun, setDistanceInputHasBegun] = useState(false);
  const [validatingDistance, setValidatingDistance] = useState(false);

  const distanceInputRef = useRef<HTMLInputElement | null>(null);

  const scalePickingState = useAppSelector((state) => state.snappingUnderlay);
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const basePoint = scalePickingState.scalePicking?.basePoint;
  const endPoint = scalePickingState.scalePicking?.endPoint;

  const isPickingBase =
    pickingObjectInfo?.type === 'pickingLayoutPoint' && pickingObjectInfo.target === 'uploaded-lines-scale-base';
  const isPickingEnd =
    pickingObjectInfo?.type === 'pickingLayoutPoint' && pickingObjectInfo.target === 'uploaded-lines-scale-end';

  const bothPointsPicked = !!basePoint && !!endPoint;

  useEffect(() => {
    if (bothPointsPicked) {
      distanceInputRef.current?.focus();
    }
  }, [bothPointsPicked]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Alert severity="info" variant="outlined">
        Select two points in the scene and input the correct distance between them to scale underlay segments
        accordingly.
      </Alert>

      <LayoutPointPickerField
        label="Point A"
        target="uploaded-lines-scale-base"
        value={basePoint}
        pending={isPickingEnd && !basePoint}
        onCancel={() => dispatch(cancelPickingUnderlayScale())}
      />

      <LayoutPointPickerField
        label="Point B"
        target="uploaded-lines-scale-end"
        value={endPoint}
        pending={isPickingBase && !endPoint}
        onCancel={() => dispatch(cancelPickingUnderlayScale())}
      />

      <DistanceField
        fullWidth
        inputRef={distanceInputRef}
        canBeNull={distance == null && !distanceInputHasBegun}
        label="Distance Between Points"
        value={distance}
        onSubmit={setDistance}
        onDisplayValueChange={() => setDistanceInputHasBegun(true)}
        error={validatingDistance && distance == null ? 'invalid distance' : undefined}
        onFocus={() => setValidatingDistance(false)}
      />

      <Stack direction="row" spacing={1}>
        <Button fullWidth size="small" variant="outlined" onClick={() => dispatch(cancelPickingUnderlayScale())}>
          Cancel
        </Button>

        <Button
          fullWidth
          variant="contained"
          disabled={!distanceInputHasBegun || !basePoint || !endPoint || point2DEquals(basePoint, endPoint)}
          onClick={() => {
            if (distance == null) {
              setValidatingDistance(true);
              distanceInputRef.current?.focus();
              return;
            }

            if (!basePoint || !endPoint || point2DEquals(basePoint, endPoint)) {
              throw new Error('button clicked when disabled');
            }

            const measuredDistance = point2DDistanceTo(basePoint, endPoint);
            const newScale = (distance * props.underlay.scale) / measuredDistance;

            dispatch(saveSnappingUnderlay({ ...props.underlay, scale: newScale }));

            dispatch(snappingUnderlaySlice.actions.scalePickingCleared());
            dispatch(clearPickingObject());
            focusCameraToVisibleSnappingGuideLines();
          }}
        >
          Apply Scale
        </Button>
      </Stack>
    </Box>
  );
}
